import React from 'react';
import {
    TextField,
    InputAdornment,
    Box,
    FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledInput = styled(TextField)(({ theme }) => ({
    // Override theme defaults for this component
    '& .MuiInputLabel-root': {
        // Reset the forced shrink from theme
        position: 'absolute',
        transform: 'translate(14px, 9px) scale(1)',
        fontSize: '0.875rem',
        color: '#455a64',
        fontWeight: 500,
        pointerEvents: 'none',
        backgroundColor: 'transparent', // Important for notch
        
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },

        '&.Mui-error': {
            color: theme.palette.error.main,
        },

        // Proper shrink behavior when focused or has value
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: '#ffffff', // White background to cover the border
            padding: '0 4px', // Padding to extend the white area
            marginLeft: '-4px', // Offset the padding
        },
    },

    '& .MuiOutlinedInput-root': {
        backgroundColor: '#ffffff',
        fontSize: '0.875rem',

        '& input': {
            padding: '8.5px 14px',
            height: 'auto',

            '&::placeholder': {
                color: '#98a2b3',
                opacity: 0, // Hide placeholder when label is visible
            },
        },

        '& textarea': {
            padding: '8.5px 14px',
        },

        '& fieldset': {
            borderColor: '#d0d5dd',
            transition: 'all 0.2s ease-in-out',
        },

        '&:hover fieldset': {
            borderColor: '#98a2b3',
        },

        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1.5px',
        },

        '&.Mui-error fieldset': {
            borderColor: theme.palette.error.main,
        },

        '&.Mui-disabled': {
            backgroundColor: '#f9fafb',
            '& fieldset': {
                borderColor: '#e5e7eb',
            },
        },

        '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
            WebkitTextFillColor: '#000',
        },
    },

    // Ensure notch is properly rendered
    '& .MuiOutlinedInput-notchedOutline': {
        '& legend': {
            // This creates the gap in the border for the label
            maxWidth: '100%',
            transition: 'max-width 100ms cubic-bezier(0.0, 0, 0.2, 1) 50ms',
        },
    },

    // Show placeholder only when label is shrunk
    '& .MuiInputLabel-shrink ~ .MuiOutlinedInput-root input::placeholder': {
        opacity: 1,
    },

    // Filled variant
    '& .MuiFilledInput-root': {
        backgroundColor: '#f9fafb',
        fontSize: '0.875rem',
        
        '&:before, &:after': {
            borderBottomColor: '#d0d5dd',
        },

        '&:hover:before': {
            borderBottomColor: '#98a2b3',
        },

        '&.Mui-focused:after': {
            borderBottomColor: theme.palette.primary.main,
        },

        '& input': {
            padding: '25px 12px 8px',
        },
    },

    // Standard variant
    '& .MuiInput-root': {
        fontSize: '0.875rem',
        
        '&:before': {
            borderBottomColor: '#d0d5dd',
        },

        '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: '#98a2b3',
        },

        '&.Mui-focused:after': {
            borderBottomColor: theme.palette.primary.main,
        },

        '& input': {
            padding: '4px 0 5px',
        },
    },
}));

const LsTextField = ({
    label,
    required = false,
    error = false,
    helperText,
    placeholder,
    disabled = false,
    multiline = false,
    rows = 4,
    type = 'text',
    startAdornment,
    endAdornment,
    fullWidth = true,
    value,
    onChange,
    variant = 'outlined',
    size = 'small',
    InputLabelProps,
    ...props
}) => {
    return (
        <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
            <StyledInput
                label={label}
                required={required}
                placeholder={placeholder}
                error={error}
                disabled={disabled}
                multiline={multiline}
                rows={multiline ? rows : undefined}
                type={type}
                fullWidth={fullWidth}
                value={value}
                onChange={onChange}
                variant={variant}
                size={size}
                InputLabelProps={{
                    // Override theme's forced shrink: true
                    shrink: undefined,
                    ...InputLabelProps,
                }}
                InputProps={{
                    ...(startAdornment && {
                        startAdornment: (
                            <InputAdornment position="start">
                                {startAdornment}
                            </InputAdornment>
                        ),
                    }),
                    ...(endAdornment && {
                        endAdornment: (
                            <InputAdornment position="end">
                                {endAdornment}
                            </InputAdornment>
                        ),
                    }),
                }}
                {...props}
            />

            {helperText && (
                <FormHelperText
                    error={error}
                    sx={{
                        ml: variant === 'outlined' ? '14px' : 0,
                        mt: '4px',
                        fontSize: '0.75rem',
                        lineHeight: '1.4',
                    }}
                >
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default LsTextField;